import React, { Component } from 'react';
import { motion } from 'framer-motion';
import { Trash2, MessageSquare, Megaphone, Droplets, FileText, Bot } from 'lucide-react';
import { useLanguage } from './LanguageContext';
const reveal = {
  initial: {
    opacity: 0,
    y: 30
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-80px'
  },
  transition: {
    duration: 0.7
  }
};
type Module = {
  icon: ComponentType<{
    className?: string;
    strokeWidth?: number;
  }>;
  x: number;
  y: number;
  port: {
    x: number;
    y: number;
  };
  edge: {
    x: number;
    y: number;
  };
  title: string;
  desc: string;
};
function ModuleCard({
  m
}: {
  m: Module;
}) {
  const Icon = m.icon;
  return <div className="w-full h-full border border-[#E4E6EC] bg-white p-4 flex gap-3.5 items-start" data-id="element-522">
      <div className="w-9 h-9 shrink-0 bg-[#4F46E5]/[0.08] flex items-center justify-center" data-id="element-523">
        <Icon className="w-[18px] h-[18px] text-[#4F46E5]" strokeWidth={1.75} data-id="element-524" />
      </div>
      <div className="min-w-0" data-id="element-525">
        <div className="font-sans text-[15px] font-semibold text-[#121212] leading-tight mb-1" data-id="element-526">
          {m.title}
        </div>
        <div className="font-sans text-[12.5px] text-[#5B6478] leading-snug" data-id="element-527">
          {m.desc}
        </div>
      </div>
    </div>;
}
export function OneFoundation() {
  const {
    lang
  } = useLanguage();
  const isFR = lang === 'FR';
  const MODULES: Module[] = [{
    icon: Trash2,
    x: 20,
    y: 30,
    port: {
      x: 360,
      y: 110
    },
    edge: {
      x: 526.3,
      y: 262.4
    },
    title: isFR ? 'AI Collecte' : 'AI Collect',
    desc: isFR ? 'Collecte intelligente des matières résiduelles avec analyses.' : 'Smart waste collection with insights.'
  }, {
    icon: MessageSquare,
    x: 20,
    y: 250,
    port: {
      x: 360,
      y: 330
    },
    edge: {
      x: 500,
      y: 330
    },
    title: isFR ? 'AI Requêtes (311)' : 'AI Requests (311)',
    desc: isFR ? 'Gestion intelligente des requêtes citoyennes de bout en bout.' : 'Intelligent request management, end to end.'
  }, {
    icon: Megaphone,
    x: 20,
    y: 470,
    port: {
      x: 360,
      y: 550
    },
    edge: {
      x: 526.3,
      y: 397.6
    },
    title: 'AI Communications',
    desc: isFR ? 'Communications ciblées et personnalisées à grande échelle.' : 'Targeted, personalized communications at scale.'
  }, {
    icon: Droplets,
    x: 840,
    y: 30,
    port: {
      x: 840,
      y: 110
    },
    edge: {
      x: 673.7,
      y: 262.4
    },
    title: isFR ? 'AI Compteurs intelligents' : 'AI Smart Water',
    desc: isFR ? "Surveillance des réseaux d'eau, détection des fuites et analyses prédictives." : 'Water intelligence and leak detection.'
  }, {
    icon: FileText,
    x: 840,
    y: 250,
    port: {
      x: 840,
      y: 330
    },
    edge: {
      x: 700,
      y: 330
    },
    title: isFR ? 'AI Permis' : 'AI Permits',
    desc: isFR ? 'Automatisation et gestion intelligente des demandes de permis.' : 'Permit automation and approvals.'
  }, {
    icon: Bot,
    x: 840,
    y: 470,
    port: {
      x: 840,
      y: 550
    },
    edge: {
      x: 673.7,
      y: 397.6
    },
    title: 'AI Agent · Web/Mobile',
    desc: isFR ? 'Agent conversationnel intelligent accessible sur tous les canaux.' : 'Conversational AI agent across channels.'
  }];
  return <section className="bg-white border-t border-[#E4E6EC] py-28 lg:py-36 px-6" data-id="element-528">
      {/* Header */}
      <div className="max-w-4xl mx-auto text-center mb-16" data-id="element-529">
        <motion.div {...reveal} className="font-sans text-xs font-semibold tracking-[0.2em] text-[#4F46E5] uppercase mb-8" data-id="element-530">
          {isFR ? 'Une plateforme' : 'One platform'}
        </motion.div>
        <motion.h2 {...reveal} transition={{
        duration: 0.7,
        delay: 0.05
      }} className="font-serif text-4xl md:text-5xl lg:text-[3.25rem] text-[#121212] leading-[1.12] tracking-tight mb-6" data-id="element-531">
          {isFR ? <>
              Une fondation.
              <br data-id="element-532" />
              Des modules qui s'<span className="text-[#4F46E5]" data-id="element-533">ajoutent</span>
              .
            </> : <>
              One foundation.
              <br data-id="element-534" />
              Every <span className="text-[#4F46E5]" data-id="element-535">module</span>.
            </>}
        </motion.h2>
        <motion.p {...reveal} transition={{
        duration: 0.7,
        delay: 0.1
      }} className="font-sans text-lg md:text-xl text-[#121212]/60 leading-relaxed max-w-2xl mx-auto" data-id="element-536">
          {isFR ? 'Chaque capacité IA partage la même identité vérifiée, le même moteur d’intelligence de confiance et la même fondation de données municipales. Déployez une fois. Étendez ensuite.' : 'Every AI capability shares the same verified identity, trusted intelligence engine and municipal data foundation. Deploy once. Expand from there.'}
        </motion.p>
      </div>

      {/* Diagram — desktop SVG with foreignObject cards */}
      <motion.div {...reveal} transition={{
      duration: 0.8,
      delay: 0.15
    }} className="max-w-6xl mx-auto" data-id="element-537">
        {isFR ? <img src="https://cdn.magicpatterns.com/uploads/4uvF3ebffmEG2nDN35gKBC/ChatGPT_Image_7_juil._2026,_16_27_07.png" alt="Six capacités IA — AI Collecte, AI Requêtes (311), AI Communications, AI Compteurs intelligents, AI Permis et AI Agent (Web/Mobile) — connectées au moteur d'intelligence de confiance ATNA." className="hidden md:block w-full h-auto object-contain" data-id="element-538" /> : <img src="https://cdn.magicpatterns.com/uploads/hmZcqQFLF3da878AQzcpAc/ChatGPT_Image_7_juil._2026,_16_23_18.png" alt="Six AI capabilities — AI Collect, AI Requests (311), AI Communications, AI Smart Water, AI Permits and AI Agent (Web/Mobile) — connected to the ATNA trusted intelligence engine." className="hidden md:block w-full h-auto object-contain" data-id="element-539" />}

        {/* Diagram — mobile fallback */}
        <div className="md:hidden" data-id="element-540">
          <div className="border border-[#4F46E5]/40 bg-[#4F46E5]/[0.03] p-6 text-center mb-6" data-id="element-541">
            <div className="font-serif text-2xl text-[#121212]" data-id="element-542">ATNA</div>
            <div className="font-sans text-[11px] tracking-[0.15em] uppercase text-[#4F46E5] font-semibold mt-1" data-id="element-543">
              {isFR ? "Moteur d'intelligence de confiance" : 'Trusted intelligence engine'}
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4" data-id="element-544">
            {MODULES.map((m, i) => <div key={'m' + i} className="h-auto" data-id="element-545">
                <ModuleCard m={m} data-id="element-546" />
              </div>)}
          </div>
          <p className="mt-8 text-center font-sans text-sm text-[#5B6478]" data-id="element-547">
            {isFR ? 'Une intelligence vérifiée au service de chaque collectivité.' : 'Verified intelligence in service of every community.'}
          </p>
        </div>
      </motion.div>

      {/* Bottom message */}
      <motion.div {...reveal} transition={{
      duration: 0.7,
      delay: 0.2
    }} className="max-w-3xl mx-auto text-center mt-20" data-id="element-548">
        <p className="font-serif text-2xl md:text-3xl text-[#121212] leading-snug mb-4" data-id="element-549">
          {isFR ? 'Plutôt que de bâtir une IA différente pour chaque service, BCITI bâtit chaque service sur la même fondation de confiance.' : 'Instead of building a different AI for every municipal service, BCITI builds every service on the same trusted foundation.'}
        </p>
        <p className="font-sans text-sm font-semibold tracking-[0.16em] uppercase text-[#4F46E5]" data-id="element-550">
          {isFR ? 'Identité · Vérification · Exécution' : 'Identity · Verification · Execution'}
        </p>
      </motion.div>
    </section>;
}